import { useCallback, type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import { ApprovalFormTable } from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import type { VehicleCostEmployee } from '@/utils/approval/vehicleCost';
import { VehicleCostRoot } from './styles';

type TableProps = {
  employee: VehicleCostEmployee;
  t: TFunction;
};

/** 레거시 #emp_info 안쪽 마크업(class 동일) */
const VehicleCostEmployeeTable = ({ employee, t }: TableProps) => (
  <>
    <label className="label">{t('임직원 정보')}</label>
    <div className="textarea">
      <table className="table table-orders table-bordered table-striped table-responsive no-margin">
        <tbody>
          <tr>
            <th className="text-center">{t('이름')}</th>
            <th className="text-center">{t('사번')}</th>
            <th className="text-center">{t('소속')}</th>
            <th className="text-center">{t('입사일')}</th>
          </tr>
          <tr>
            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
              {employee.name}
            </td>
            <td className="text-center">
              <span id="emp_no" data-value={employee.employeeNumber}>
                {employee.employeeNumber}
              </span>
            </td>
            <td className="text-center">{employee.affiliation}</td>
            <td className="text-center">{employee.enterDate}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </>
);

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildVehicleCostContentHtml = (
  employee: VehicleCostEmployee,
  t: TFunction,
): string =>
  renderToStaticMarkup(<VehicleCostEmployeeTable employee={employee} t={t} />);

const DROP_TAGS = new Set(['style', 'script', 'link']);
// 레거시 content 의 줄바꿈 공백이 표 태그 바로 아래 남으면 React 경고
const TABLE_PARTS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr']);
const isBlankText = (node: ReactNode) =>
  typeof node === 'string' && !node.trim();

/** 저장 content(레거시 문서 포함) 그대로 — 표·제목만 공통 양식 컴포넌트로 */
export const VehicleCostSnapshot = ({ content }: { content: string }) => {
  const transform = useCallback<ElementTransform>(
    ({ tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (TABLE_PARTS.has(tagName)) {
        const kept = children.filter((c) => !isBlankText(c));
        children.splice(0, children.length, ...kept);
      }
      const key = props.key as string;
      if (tagName === 'label') {
        return (
          <InfoSectionTitle key={key} sx={{ mb: 1 }}>
            {children}
          </InfoSectionTitle>
        );
      }
      if (tagName === 'table') {
        return <ApprovalFormTable key={key}>{children}</ApprovalFormTable>;
      }
      return undefined;
    },
    [],
  );
  const rendered = useHtmlToReact(content, { transform });
  return <VehicleCostRoot>{rendered}</VehicleCostRoot>;
};
